import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Progress } from '../components/ui/progress';
import {
  ClipboardCheck, FileCheck, FileX, Clock, Send, Search,
  Loader2, AlertCircle, CheckCircle2, Mail, Brain, RefreshCw,
  ChevronRight, Eye, Upload, ArrowRight, Sparkles
} from 'lucide-react';
import { toast } from 'sonner';
import { brainDocProcessorAPI, docRequirementsAPI } from '../lib/api';
import api from '../lib/api';

function StatusBadge({ status }) {
  const config = {
    received: { label: 'Received', cls: 'bg-emerald-500/10 text-emerald-600 border-emerald-500/30' },
    pending: { label: 'Pending', cls: 'bg-amber-500/10 text-amber-600 border-amber-500/30' },
    rejected: { label: 'Rejected', cls: 'bg-red-500/10 text-red-600 border-red-500/30' },
  };
  const c = config[status] || { label: status, cls: 'bg-muted text-foreground' };
  return <Badge variant="outline" className={`text-xs capitalize ${c.cls}`}>{c.label}</Badge>;
}

function VerifiedByBadge({ by }) {
  if (!by) return null;
  if (by === 'brain_auto') return <Badge variant="outline" className="text-xs bg-violet-500/10 text-violet-600 border-violet-500/30"><Brain className="h-3 w-3 mr-1" />AI Verified</Badge>;
  return <Badge variant="outline" className="text-xs"><CheckCircle2 className="h-3 w-3 mr-1" />Manual</Badge>;
}

// ─── Lead Selector ──────────────────────────────────────
function LeadSelector({ selectedLead, onSelect }) {
  const [leads, setLeads] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);

  const loadLeads = useCallback(async (q) => {
    setLoading(true);
    try {
      const params = { per_page: 20 };
      if (q) params.search = q;
      const res = await api.get('/api/leads/', { params });
      const data = Array.isArray(res.data) ? res.data : (res.data?.leads || res.data?.data || []);
      setLeads(data);
    } catch {
      setLeads([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadLeads(''); }, [loadLeads]);

  return (
    <div className="space-y-2" data-testid="lead-selector">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Search leads..."
          value={search}
          onChange={e => { setSearch(e.target.value); loadLeads(e.target.value); }}
          className="pl-9"
          data-testid="lead-search-input"
        />
      </div>
      <div className="max-h-60 overflow-y-auto space-y-1 border rounded-lg p-1">
        {loading && <p className="text-sm text-muted-foreground text-center py-3"><Loader2 className="h-4 w-4 animate-spin inline mr-1" /> Loading...</p>}
        {!loading && leads.length === 0 && <p className="text-sm text-muted-foreground text-center py-3">No leads found</p>}
        {leads.map(lead => (
          <button
            key={lead.id}
            onClick={() => onSelect(lead)}
            className={`w-full text-left px-3 py-2 rounded-md text-sm hover:bg-muted/50 transition-colors flex items-center justify-between ${selectedLead?.id === lead.id ? 'bg-primary/10 border border-primary/30' : ''}`}
            data-testid={`lead-option-${lead.id}`}
          >
            <div>
              <p className="font-medium">{lead.name || 'Unnamed'}</p>
              <p className="text-xs text-muted-foreground">{lead.email || 'No email'} {lead.company ? `· ${lead.company}` : ''}</p>
            </div>
            {selectedLead?.id === lead.id && <CheckCircle2 className="h-4 w-4 text-primary shrink-0" />}
          </button>
        ))}
      </div>
    </div>
  );
}

// ─── Requirement Row ────────────────────────────────────
function RequirementRow({ req, leadId, onRefresh }) {
  const [marking, setMarking] = useState(false);

  const handleMarkReceived = async () => {
    setMarking(true);
    try {
      await docRequirementsAPI.markReceived(leadId, { doc_name: req.name });
      toast.success(`"${req.name}" marked as received`);
      onRefresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to mark as received');
    } finally {
      setMarking(false);
    }
  };

  return (
    <div className="flex items-center justify-between py-2.5 px-3 border-b last:border-b-0 hover:bg-muted/20 transition-colors" data-testid={`req-row-${req.name}`}>
      <div className="flex items-center gap-3 min-w-0 flex-1">
        {req.status === 'received' ? (
          <CheckCircle2 className="h-5 w-5 text-emerald-500 shrink-0" />
        ) : (
          <Clock className="h-5 w-5 text-amber-500 shrink-0" />
        )}
        <div className="min-w-0">
          <p className="text-sm font-medium truncate">{req.name}</p>
          <div className="flex items-center gap-2 mt-0.5">
            {req.required ? <Badge variant="outline" className="text-[10px]">Required</Badge> : <Badge variant="outline" className="text-[10px] opacity-50">Optional</Badge>}
            <VerifiedByBadge by={req.verified_by} />
            {req.reminder_count > 0 && <span className="text-[10px] text-muted-foreground">{req.reminder_count} reminder(s) sent</span>}
          </div>
        </div>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <StatusBadge status={req.status} />
        {req.status === 'pending' && (
          <Button size="sm" variant="outline" onClick={handleMarkReceived} disabled={marking} className="h-7 text-xs" data-testid={`mark-received-${req.name}`}>
            {marking ? <Loader2 className="h-3 w-3 animate-spin" /> : <><CheckCircle2 className="h-3 w-3 mr-1" /> Mark Received</>}
          </Button>
        )}
        {req.status === 'received' && req.received_at && (
          <span className="text-xs text-muted-foreground">{new Date(req.received_at).toLocaleDateString()}</span>
        )}
      </div>
    </div>
  );
}

// ─── Simulate Inbound Email Dialog ──────────────────────
function SimulateInboundDialog({ open, onClose, leadId, onDone }) {
  const [form, setForm] = useState({
    from_email: '', subject: '', body: '',
    attachments: [{ filename: '', content_type: 'application/pdf' }],
  });
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);

  const addAttachment = () => {
    setForm(f => ({ ...f, attachments: [...f.attachments, { filename: '', content_type: 'application/pdf' }] }));
  };

  const updateAttachment = (idx, key, val) => {
    const updated = [...form.attachments];
    updated[idx] = { ...updated[idx], [key]: val };
    setForm(f => ({ ...f, attachments: updated }));
  };

  const removeAttachment = (idx) => {
    if (form.attachments.length <= 1) return;
    setForm(f => ({ ...f, attachments: f.attachments.filter((_, i) => i !== idx) }));
  };

  const handleSubmit = async () => {
    if (!form.from_email) { toast.error('From email is required'); return; }
    if (!form.attachments.some(a => a.filename)) { toast.error('At least one attachment filename is required'); return; }
    setLoading(true);
    setResults(null);
    try {
      const payload = {
        lead_id: leadId,
        from_email: form.from_email,
        subject: form.subject,
        body: form.body,
        attachments: form.attachments.filter(a => a.filename).map(a => ({
          ...a,
          doc_id: `sim-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        })),
      };
      const res = await brainDocProcessorAPI.registerInboundEmail(payload);
      setResults(res.data);
      toast.success('Email processed by Brain!');
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to process email');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Mail className="h-5 w-5 text-primary" />
            Simulate Inbound Email
          </DialogTitle>
          <DialogDescription>Simulate receiving an email reply with attachments. The Brain will classify them against requirements.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div>
            <Label>From Email *</Label>
            <Input value={form.from_email} onChange={e => setForm(f => ({ ...f, from_email: e.target.value }))} placeholder="lead@company.com" className="mt-1" data-testid="inbound-from-email" />
          </div>
          <div>
            <Label>Subject</Label>
            <Input value={form.subject} onChange={e => setForm(f => ({ ...f, subject: e.target.value }))} placeholder="Re: Documents Required" className="mt-1" data-testid="inbound-subject" />
          </div>
          <div>
            <Label>Body</Label>
            <Input value={form.body} onChange={e => setForm(f => ({ ...f, body: e.target.value }))} placeholder="Please find attached..." className="mt-1" data-testid="inbound-body" />
          </div>
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label>Attachments</Label>
              <Button size="sm" variant="outline" onClick={addAttachment} className="h-7 text-xs"><Upload className="h-3 w-3 mr-1" /> Add</Button>
            </div>
            {form.attachments.map((att, idx) => (
              <div key={idx} className="flex items-center gap-2">
                <Input value={att.filename} onChange={e => updateAttachment(idx, 'filename', e.target.value)} placeholder="e.g. GST_Certificate.pdf" className="flex-1" data-testid={`att-filename-${idx}`} />
                <Select value={att.content_type} onValueChange={v => updateAttachment(idx, 'content_type', v)}>
                  <SelectTrigger className="w-36"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="application/pdf">PDF</SelectItem>
                    <SelectItem value="image/jpeg">JPEG</SelectItem>
                    <SelectItem value="image/png">PNG</SelectItem>
                    <SelectItem value="application/vnd.openxmlformats-officedocument.wordprocessingml.document">DOCX</SelectItem>
                    <SelectItem value="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet">XLSX</SelectItem>
                  </SelectContent>
                </Select>
                {form.attachments.length > 1 && (
                  <Button size="sm" variant="ghost" onClick={() => removeAttachment(idx)} className="h-8 w-8 p-0 text-red-500">
                    <FileX className="h-4 w-4" />
                  </Button>
                )}
              </div>
            ))}
          </div>

          {/* Results */}
          {results && (
            <div className="border rounded-lg p-4 bg-muted/10 space-y-3" data-testid="classification-results">
              <div className="flex items-center gap-2">
                <Brain className="h-5 w-5 text-violet-500" />
                <h4 className="font-semibold text-sm">Brain Classification Results</h4>
              </div>
              {results.classification_results?.map((r, i) => (
                <div key={i} className="flex items-center justify-between py-2 border-b last:border-b-0">
                  <div>
                    <p className="text-sm font-medium">{r.filename}</p>
                    <p className="text-xs text-muted-foreground">{r.reasoning}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    {r.matched_requirement && <Badge variant="outline" className="text-xs">{r.matched_requirement}</Badge>}
                    <Badge variant={r.action === 'auto_accept' ? 'default' : r.action === 'manual_review' ? 'secondary' : 'outline'} className={`text-xs ${r.action === 'auto_accept' ? 'bg-emerald-600' : ''}`}>
                      {r.confidence}% · {r.action?.replace('_', ' ')}
                    </Badge>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Close</Button>
          {!results && (
            <Button onClick={handleSubmit} disabled={loading} data-testid="process-inbound-btn">
              {loading ? <><Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> Processing with Brain...</> : <><Brain className="h-4 w-4 mr-1.5" /> Process Email</>}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Apply Template Dialog ──────────────────────────────
function ApplyTemplateDialog({ open, onClose, leadId, onApplied }) {
  const [industries, setIndustries] = useState([]);
  const [selectedIndustry, setSelectedIndustry] = useState('');
  const [direction, setDirection] = useState('inbound');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) {
      docRequirementsAPI.templates().then(r => {
        setIndustries(r.data?.industries || []);
      }).catch(() => {});
    }
  }, [open]);

  const handleApply = async () => {
    if (!selectedIndustry) { toast.error('Select an industry'); return; }
    setLoading(true);
    try {
      await docRequirementsAPI.applyToLead({ lead_id: leadId, industry: selectedIndustry, direction });
      toast.success(`${selectedIndustry} template applied!`);
      onApplied();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to apply template');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileCheck className="h-5 w-5 text-primary" />
            Apply Document Template
          </DialogTitle>
          <DialogDescription>Select an industry template to define required documents for this lead.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div>
            <Label>Industry</Label>
            <Select value={selectedIndustry} onValueChange={setSelectedIndustry}>
              <SelectTrigger className="mt-1" data-testid="template-industry-select"><SelectValue placeholder="Select industry..." /></SelectTrigger>
              <SelectContent>
                {industries.map(ind => <SelectItem key={ind} value={ind}>{ind}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Direction</Label>
            <Select value={direction} onValueChange={setDirection}>
              <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="inbound">Inbound (Request FROM lead)</SelectItem>
                <SelectItem value="outbound">Outbound (Send TO lead)</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={handleApply} disabled={loading} data-testid="apply-template-btn">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Apply Template'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Main Page ──────────────────────────────────────────
export default function DocVerification() {
  const [selectedLead, setSelectedLead] = useState(null);
  const [verification, setVerification] = useState(null);
  const [loading, setLoading] = useState(false);
  const [followupLoading, setFollowupLoading] = useState(false);
  const [showInbound, setShowInbound] = useState(false);
  const [showApplyTemplate, setShowApplyTemplate] = useState(false);

  const loadVerification = useCallback(async (leadId) => {
    if (!leadId) return;
    setLoading(true);
    try {
      const res = await brainDocProcessorAPI.getVerificationSummary(leadId);
      setVerification(res.data);
    } catch {
      toast.error('Failed to load verification data');
    } finally {
      setLoading(false);
    }
  }, []);

  const handleLeadSelect = (lead) => {
    setSelectedLead(lead);
    setVerification(null);
    loadVerification(lead.id);
  };

  const handleTriggerFollowup = async () => {
    if (!selectedLead) return;
    setFollowupLoading(true);
    try {
      const res = await brainDocProcessorAPI.triggerFollowup(selectedLead.id);
      const d = res.data;
      if (d.status === 'all_complete') {
        toast.success('All documents received!');
      } else {
        toast.success(`Follow-up draft created: "${d.subject}"`);
      }
      loadVerification(selectedLead.id);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to trigger follow-up');
    } finally {
      setFollowupLoading(false);
    }
  };

  const summary = verification?.summary || {};
  const requirements = verification?.requirements || [];
  const hasRequirements = verification?.has_requirements;

  return (
    <div className="space-y-6 p-1" data-testid="doc-verification-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2.5">
            <ClipboardCheck className="h-6 w-6 text-primary" />
            Document Verification
          </h1>
          <p className="text-sm text-muted-foreground mt-1">Track and verify required documents from leads using the AI Brain</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left: Lead Selector */}
        <div className="lg:col-span-1">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Select Lead</CardTitle>
              <CardDescription>Choose a lead to view document requirements</CardDescription>
            </CardHeader>
            <CardContent>
              <LeadSelector selectedLead={selectedLead} onSelect={handleLeadSelect} />
            </CardContent>
          </Card>
        </div>

        {/* Right: Verification Dashboard */}
        <div className="lg:col-span-2 space-y-4">
          {!selectedLead && (
            <Card className="border-dashed">
              <CardContent className="py-16 text-center">
                <ClipboardCheck className="h-12 w-12 text-muted-foreground/30 mx-auto mb-4" />
                <p className="font-medium">Select a lead to begin</p>
                <p className="text-sm text-muted-foreground mt-1">Choose a lead from the left panel to view or manage document requirements</p>
              </CardContent>
            </Card>
          )}

          {selectedLead && loading && (
            <Card>
              <CardContent className="py-12 text-center">
                <Loader2 className="h-8 w-8 animate-spin text-primary mx-auto" />
                <p className="text-sm text-muted-foreground mt-3">Loading verification data...</p>
              </CardContent>
            </Card>
          )}

          {selectedLead && !loading && verification && (
            <>
              {/* Lead Info + Actions */}
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-lg font-semibold">{selectedLead.name}</h2>
                  <p className="text-sm text-muted-foreground">{selectedLead.email} {selectedLead.company ? `· ${selectedLead.company}` : ''}</p>
                </div>
                <div className="flex items-center gap-2">
                  {hasRequirements && (
                    <>
                      <Button size="sm" variant="outline" onClick={() => setShowInbound(true)} className="gap-1.5" data-testid="simulate-inbound-btn">
                        <Mail className="h-4 w-4" /> Simulate Email
                      </Button>
                      <Button size="sm" onClick={handleTriggerFollowup} disabled={followupLoading || summary.pending === 0} className="gap-1.5" data-testid="trigger-followup-btn">
                        {followupLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Sparkles className="h-4 w-4" /> AI Follow-up</>}
                      </Button>
                    </>
                  )}
                  <Button size="sm" variant={hasRequirements ? 'ghost' : 'default'} onClick={() => setShowApplyTemplate(true)} className="gap-1.5" data-testid="apply-template-btn">
                    <FileCheck className="h-4 w-4" /> {hasRequirements ? 'Change Template' : 'Apply Template'}
                  </Button>
                </div>
              </div>

              {/* Summary Stats */}
              {hasRequirements && (
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  <Card className="border-l-4 border-l-primary/60">
                    <CardContent className="py-3 px-4">
                      <p className="text-2xl font-bold">{summary.total || 0}</p>
                      <p className="text-xs text-muted-foreground">Total Required</p>
                    </CardContent>
                  </Card>
                  <Card className="border-l-4 border-l-emerald-500/60">
                    <CardContent className="py-3 px-4">
                      <p className="text-2xl font-bold text-emerald-600">{summary.received || 0}</p>
                      <p className="text-xs text-muted-foreground">Received</p>
                    </CardContent>
                  </Card>
                  <Card className="border-l-4 border-l-amber-500/60">
                    <CardContent className="py-3 px-4">
                      <p className="text-2xl font-bold text-amber-600">{summary.pending || 0}</p>
                      <p className="text-xs text-muted-foreground">Pending</p>
                    </CardContent>
                  </Card>
                  <Card className="border-l-4 border-l-violet-500/60">
                    <CardContent className="py-3 px-4">
                      <p className="text-2xl font-bold text-violet-600">{summary.completion_pct || 0}%</p>
                      <p className="text-xs text-muted-foreground">Complete</p>
                    </CardContent>
                  </Card>
                </div>
              )}

              {/* Progress */}
              {hasRequirements && (
                <Card>
                  <CardContent className="py-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium">{summary.industry || 'Custom'} Requirements</span>
                      <span className="text-sm text-muted-foreground">{summary.received || 0} / {summary.total || 0}</span>
                    </div>
                    <Progress value={summary.completion_pct || 0} className="h-2" />
                  </CardContent>
                </Card>
              )}

              {/* Requirements List */}
              {hasRequirements ? (
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base flex items-center gap-2">
                      <FileCheck className="h-4 w-4" /> Document Requirements
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-0">
                    <div className="divide-y" data-testid="requirements-list">
                      {requirements.map((req, i) => (
                        <RequirementRow key={i} req={req} leadId={selectedLead.id} onRefresh={() => loadVerification(selectedLead.id)} />
                      ))}
                    </div>
                  </CardContent>
                </Card>
              ) : (
                <Card className="border-dashed">
                  <CardContent className="py-12 text-center">
                    <AlertCircle className="h-10 w-10 text-muted-foreground/30 mx-auto mb-3" />
                    <p className="font-medium">No Requirements Set</p>
                    <p className="text-sm text-muted-foreground mt-1 max-w-sm mx-auto">Apply an industry template to define what documents this lead needs to provide</p>
                    <Button className="mt-4 gap-1.5" onClick={() => setShowApplyTemplate(true)} data-testid="apply-template-empty-btn">
                      <FileCheck className="h-4 w-4" /> Apply Template
                    </Button>
                  </CardContent>
                </Card>
              )}

              {/* Inbound Emails */}
              {verification?.inbound_emails?.length > 0 && (
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base flex items-center gap-2">
                      <Mail className="h-4 w-4" /> Inbound Emails with Attachments
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    {verification.inbound_emails.map(email => (
                      <div key={email.id} className="flex items-center justify-between p-3 border rounded-lg hover:bg-muted/20 transition-colors" data-testid={`inbound-email-${email.id}`}>
                        <div className="flex items-center gap-3">
                          <Mail className="h-4 w-4 text-muted-foreground" />
                          <div>
                            <p className="text-sm font-medium">{email.subject || '(no subject)'}</p>
                            <p className="text-xs text-muted-foreground">{email.from_email} · {email.attachment_count} attachment(s) · {new Date(email.created_at).toLocaleString()}</p>
                          </div>
                        </div>
                        <Badge variant={email.classification_status === 'completed' ? 'default' : 'secondary'} className={`text-xs ${email.classification_status === 'completed' ? 'bg-emerald-600' : ''}`}>
                          {email.classification_status}
                        </Badge>
                      </div>
                    ))}
                  </CardContent>
                </Card>
              )}
            </>
          )}
        </div>
      </div>

      {/* Dialogs */}
      {selectedLead && (
        <>
          <SimulateInboundDialog
            open={showInbound}
            onClose={() => setShowInbound(false)}
            leadId={selectedLead.id}
            onDone={() => loadVerification(selectedLead.id)}
          />
          <ApplyTemplateDialog
            open={showApplyTemplate}
            onClose={() => setShowApplyTemplate(false)}
            leadId={selectedLead.id}
            onApplied={() => loadVerification(selectedLead.id)}
          />
        </>
      )}
    </div>
  );
}
